import { RouteComponentProps, useHistory } from 'react-router-dom';
import {
    ActionsGroup,
    CardLayout,
    CardPagination,
    CardSort,
    Filters,
    HighlightText,
    MenuButton,
    NumberFormat,
    Page,
    PageSection,
    PageTextSummary,
    SearchAndFiltersLayout,
    SearchInput,
    SearchSummary
} from '@crx-dev/hss-react-components';
import React, { useCallback } from 'react';
import { useGetReturnUrl } from '../../../Infrastructure/pageState/useGetReturnUrl';
import { ReturnedRegisterSummary } from '../Registers/types/ReturnedRegisterSummary';
import { makeReturnUrl } from '../../../Infrastructure/pageState/useMakeReturnUrl';
import { usePaginationInUrl } from '../../../Infrastructure/pageState/usePaginationInUrl';
import { useQuery } from 'react-query';
import { PaginatedResponse } from '../../Common/types/PaginatedResponse';
import { makeRequest } from '@crx-dev/hss-react-infrastructure';
import ReturnedEntriesListItem from './ReturnedEntriesListItem';
import { ReturnedDrugRegisterEntry } from './types/ReturnedDrugRegisterEntry';
import { returnedFiltersConfig } from './config/returnedFilterConfig';

interface ReturnedEntriesProps extends RouteComponentProps<{ returnedRegisterId?: string }> {
    returnedRegisterSummary: ReturnedRegisterSummary;
}

function ReturnedEntriesList({ match, returnedRegisterSummary }: ReturnedEntriesProps) {
    const history = useHistory();
    const returnUrl = useGetReturnUrl() ?? '/returned-registers';
    const returnedRegisterId = match.params.returnedRegisterId as string;

    const {
        // @ts-ignore
        pageState: { page, pageSize, sortField, sortDirection, entryTypes, dateFrom, dateTo, search },
        actions: { setPage, setPageSize, setSort, setValues, setSearch }
    } = usePaginationInUrl({
        sortField: 'CreatedDateTime',
        sortDirection: 'desc',
        search: ''
    });

    const filterValues = {
        entryTypes: entryTypes ? entryTypes.split(',') : [],
        dateRange: { from: dateFrom || '', to: dateTo || '' }
    };

    const setFilterValues = useCallback(
        values => {
            setValues({
                entryTypes: values.entryTypes,
                dateFrom: values.dateRange.from,
                dateTo: values.dateRange.to
            });
        },
        [setValues]
    );

    const { data, isLoading, isFetching } = useQuery<PaginatedResponse<ReturnedDrugRegisterEntry[]>>(
        [
            'returned-entries',
            returnedRegisterId,
            page,
            pageSize,
            sortField,
            sortDirection,
            entryTypes,
            dateTo,
            dateFrom,
            search
        ],
        () =>
            makeRequest({
                method: 'GET',
                path: `/api/v1/returneddrugregister/${returnedRegisterId}/entries`,
                data: {
                    page,
                    pageSize,
                    sortField,
                    sortDirection,
                    entryTypes,
                    dateFrom,
                    dateTo,
                    search
                }
            }),
        {
            keepPreviousData: true
        }
    );

    const handleRecordEntry = useCallback(value => {
        history.push(`/returned-registers/${returnedRegisterId}/${value}?returnUrl=${makeReturnUrl()}`);
    }, []);

    const handleSearch = useCallback((value: string) => {
        setSearch(value);
    }, []);

    return (
        <Page>
            <Page.Header
                title={returnedRegisterSummary.title}
                subtitle={returnedRegisterSummary.subTitle}
                onBackClick={() => history.push(returnUrl)}
                pageActions={
                    <ActionsGroup>
                        <MenuButton
                            id="addEntry"
                            menuItems={[
                                { label: 'Returned', value: 'returned', onClick: handleRecordEntry },
                                { label: 'Destroyed', value: 'destroyed', onClick: handleRecordEntry }
                            ]}
                        >
                            Record Entry
                        </MenuButton>
                    </ActionsGroup>
                }
            />
            <Page.Content>
                <PageTextSummary title="Overview" isLoading={false}>
                    <>
                        The total balance is{' '}
                        <HighlightText>
                            <NumberFormat value={returnedRegisterSummary.balance} />
                        </HighlightText>{' '}
                    </>
                </PageTextSummary>
            </Page.Content>
            <PageSection>
                <SearchAndFiltersLayout
                    search={
                        <SearchInput
                            name="search-field"
                            onSearch={handleSearch}
                            placeholder="Search entries"
                            defaultValue={search}
                        />
                    }
                    filters={
                        <Filters config={returnedFiltersConfig} values={filterValues} onChange={setFilterValues} />
                    }
                    actions={[
                        <CardSort
                            value={{ field: sortField, direction: sortDirection }}
                            onChange={setSort}
                            options={[{ value: 'CreatedDateTime', label: 'Date' }]}
                        />
                    ]}
                />

                <SearchSummary
                    filtersConfig={returnedFiltersConfig}
                    filterValues={filterValues}
                    onFiltersChange={setFilterValues}
                />
                <>
                    <CardLayout columns={1} isUpdating={isFetching} isLoading={isLoading}>
                        {data &&
                            data.payload.map(item => (
                                <ReturnedEntriesListItem key={item.id} item={item as ReturnedDrugRegisterEntry} />
                            ))}
                    </CardLayout>
                    <CardPagination
                        page={page}
                        totalResults={data?.count ?? 0}
                        rowsPerPage={pageSize}
                        onPageChange={setPage}
                        onRowsPerPageChange={setPageSize}
                    />
                </>
            </PageSection>
        </Page>
    );
}

export default ReturnedEntriesList;
